import { AbTestingService } from "../../core/abTestingService/AbTestingService";
import { AnalyticsConstants } from "../../presentation/assets/constants/analytics.constants";
import { AnalyticsFeature } from "../analytics/analyticsFeature";
import { Constants } from "../models/Constants";
import {
  dailyCardsStateKey,
  resolveDeckAllowance,
} from "./dailyCardsExperimentPolicy";
import {
  DailyCardsAssignment,
  DailyCardsExperimentConfig,
  DeckAllowance,
} from "./models/DailyCardsExperiment";

interface DailyCardsExperimentDependencies {
  observeDefinition: (
    onChanged: (definition: DailyCardsExperimentConfig | null) => void,
    onError: (error: unknown) => void,
  ) => () => void;
  observeAssignment: (
    userId: string,
    onChanged: (assignment: DailyCardsAssignment | null) => void,
    onError: (error: unknown) => void,
  ) => () => void;
  trackUserProperty: (name: string, value: string | null) => void;
  trackDefaultEventParameters: (params: Record<string, unknown> | null) => void;
  trackEvent: (eventName: string, params?: Record<string, unknown>) => void;
}

const createDefaultDependencies = (): DailyCardsExperimentDependencies => ({
  observeDefinition: (onChanged, onError) =>
    AbTestingService.getInstance().observeDailyCardsDefinition(
      onChanged,
      onError,
    ),
  observeAssignment: (userId, onChanged, onError) =>
    AbTestingService.getInstance().observeDailyCardsAssignment(
      userId,
      onChanged,
      onError,
    ),
  trackUserProperty: (name, value) =>
    AnalyticsFeature.trackUserProperty(name, value),
  trackDefaultEventParameters: (params) =>
    AnalyticsFeature.trackDefaultEventParameters(params),
  trackEvent: (eventName, params) =>
    AnalyticsFeature.trackEvent(eventName, params),
});

/**
 * Daily free cards A/B test (RTDB-driven, cross-platform).
 *
 * The variant is assigned server-side per Firebase Auth user (see
 * `backend/functions/src/core/daily-cards-experiment.ts`) so iOS, web and
 * both partners of a couple agree. This feature observes the definition
 * (`abTesting/definitions/dailyCards`) and this user's assignment
 * (`abTesting/assignments/{uid}/dailyCards`) live, resolves the deck
 * allowance for `DeckSession`, tells open deck sessions when it changes, and
 * tags analytics: a GA4 user property plus a default event parameter on
 * every event, and one `experiment_exposure` per session on the first deck
 * build under a variant. Mirrors the rn-app feature of the same name.
 */
export class DailyCardsExperimentFeature {
  private static dependencies = createDefaultDependencies();
  private static unsubscribeDefinition: (() => void) | null = null;
  private static unsubscribeAssignment: (() => void) | null = null;
  private static observedUserId: string | null = null;
  private static definition: DailyCardsExperimentConfig | null = null;
  private static assignment: DailyCardsAssignment | null = null;
  private static exposedVariant: string | null = null;
  private static publishedVariant: string | null | undefined = undefined;
  private static stateKey = dailyCardsStateKey(null, null);
  private static changeListeners = new Set<() => void>();

  public static setDependenciesForTests(
    overrides: Partial<DailyCardsExperimentDependencies>,
  ): void {
    this.dependencies = { ...this.dependencies, ...overrides };
  }

  public static resetForTests(): void {
    this.stopForAuthenticatedUser();
    this.changeListeners.clear();
    this.dependencies = createDefaultDependencies();
  }

  public static startForAuthenticatedUser(userId: string): void {
    this.teardown();
    this.observedUserId = userId;
    this.exposedVariant = null;
    try {
      // Definitions require a signed-in reader, so both observers share the
      // auth session's lifetime.
      this.unsubscribeDefinition = this.dependencies.observeDefinition(
        (definition) => {
          if (this.observedUserId !== userId) {
            return;
          }
          this.definition = definition;
          this.handleStateChanged();
        },
        () => undefined,
      );
      this.unsubscribeAssignment = this.dependencies.observeAssignment(
        userId,
        (assignment) => {
          if (this.observedUserId !== userId) {
            return;
          }
          this.assignment = assignment;
          this.handleStateChanged();
        },
        () => undefined,
      );
    } catch {
      // No session yet — the deck falls back to the base constants.
      this.teardown();
    }
  }

  public static stopForAuthenticatedUser(): void {
    this.teardown();
    this.definition = null;
    this.assignment = null;
    this.exposedVariant = null;
    this.handleStateChanged();
  }

  public static getAssignment(): DailyCardsAssignment | null {
    return this.assignment;
  }

  /** Base constants unless the experiment is on and this user is enrolled. */
  public static resolveDeckAllowance(
    constants: Constants | null | undefined,
  ): DeckAllowance {
    return resolveDeckAllowance(constants, this.definition, this.assignment);
  }

  /** Identifies how the deck is currently dealt; compare to detect a stale build. */
  public static getStateKey(): string {
    return this.stateKey;
  }

  /** Fires when the deck would be dealt differently (kill switch, late assignment). */
  public static addChangeListener(listener: () => void): () => void {
    this.changeListeners.add(listener);
    return () => {
      this.changeListeners.delete(listener);
    };
  }

  /** Once per session: the user has actually seen a deck built under the variant. */
  public static trackExposure(variant: string): void {
    if (this.exposedVariant === variant) {
      return;
    }
    this.exposedVariant = variant;
    this.dependencies.trackEvent(AnalyticsConstants.events.experimentExposure, {
      [AnalyticsConstants.params.experiment]:
        AnalyticsConstants.values.experiments.dailyCards,
      [AnalyticsConstants.params.variant]: variant,
    });
  }

  private static teardown(): void {
    this.unsubscribeDefinition?.();
    this.unsubscribeAssignment?.();
    this.unsubscribeDefinition = null;
    this.unsubscribeAssignment = null;
    this.observedUserId = null;
  }

  private static handleStateChanged(): void {
    // Analytics carry the variant only while the experiment is in effect, so
    // a switched-off experiment stops tagging events.
    const allowance = resolveDeckAllowance(
      null,
      this.definition,
      this.assignment,
    );
    this.publishAnalyticsTags(allowance.variant);

    const nextKey = dailyCardsStateKey(this.definition, this.assignment);
    if (nextKey === this.stateKey) {
      return;
    }
    this.stateKey = nextKey;
    this.changeListeners.forEach((listener) => listener());
  }

  /** Sends only on change — the observers re-emit on every write. */
  private static publishAnalyticsTags(variant: string | null): void {
    if (this.publishedVariant === variant) {
      return;
    }
    this.publishedVariant = variant;
    this.dependencies.trackUserProperty(
      AnalyticsConstants.userProperties.dailyCardsVariant,
      variant,
    );
    this.dependencies.trackDefaultEventParameters({
      [AnalyticsConstants.params.dailyCardsVariant]: variant,
    });
  }
}
